import {useInterval} from "../hooks";
import {useEffect, useRef, useState} from "react";
import {
    AllianceDescription, BasicAllianceDescription, DoubleEliminationSeriesDescription, MatchInfo, MatchTeam,
    PlayoffStructure,
    SeriesAllianceDescription,
    SeriesAllianceState,
    SeriesDescription, T206Timeout, TimeoutReference
} from "../javaTypes";
import {isT206DependencyTimeout, isT206Timeout} from "../timeoutUtils";

export function useSlides(count: number, interval?: number) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [didClick, setDidClick] = useState(false);
    const maxCount = useRef(count);

    useEffect(() => {
        maxCount.current = count
    }, [count]);

    useInterval(() => {
        if(maxCount.current === 0) {
            return;
        }

        setCurrentSlide((curr) => {
            return (curr + 1) % maxCount.current
        })
    }, didClick ? null : (interval == undefined ? 10000 : interval))

    useEffect(() => {
        const listener = function (e : KeyboardEvent) {
            if(e.code === 'F14') {
                setDidClick(true)
                setCurrentSlide((curr) => {
                    return (curr + 1) % maxCount.current
                })
            } else if(e.code === 'F15') {
                setDidClick(false)
            }
        }
        document.addEventListener('keydown', listener);
        return () => {
            document.removeEventListener("keydown", listener)
        }
    }, [])

    return currentSlide
}

export function isBasicAllianceDescription(description: AllianceDescription): description is BasicAllianceDescription {
    return 'alliance' in description;
}

export function isSeriesAllianceDescription(description: AllianceDescription): description is SeriesAllianceDescription {
    return 'series' in description;
}
export function isDoubleElimsSeriesDescription(description: SeriesDescription): description is DoubleEliminationSeriesDescription {
    return 'bracketHalf' in description || 'optional' in description;
}

export function bracketTargets(tournament: PlayoffStructure<SeriesDescription> | undefined, series: number | undefined) {
    if(!tournament || !series) {
        return [undefined, undefined]
    }
    const winnerTarget = tournament.series.find(p => isSeriesAllianceDescription(p.red) && p.red.series == series && p.red.state == SeriesAllianceState.WINNER || isSeriesAllianceDescription(p.blue) && p.blue.series == series && p.blue.state == SeriesAllianceState.WINNER)
    const loserTarget = tournament.series.find(p => isSeriesAllianceDescription(p.red) && p.red.series == series && p.red.state == SeriesAllianceState.LOSER || isSeriesAllianceDescription(p.blue) && p.blue.series == series && p.blue.state == SeriesAllianceState.LOSER)
    return [winnerTarget, loserTarget]
}

export function getExpectedStartTimer(nextMatch: MatchInfo<MatchTeam>, timeouts: TimeoutReference[], isApplicableTimeout: (nextMatch: MatchInfo<MatchTeam>, timeout: TimeoutReference) => boolean, nowCallback: () => number) {
    let applicable = timeouts.filter(to => isApplicableTimeout(nextMatch, to));
    if (applicable.length === 0) {
        return undefined;
    }
    const getTimeoutEnd = (to: TimeoutReference) => ((isT206Timeout(to) && (to as T206Timeout).awaitingScorePost) || isT206DependencyTimeout(to) ? nowCallback() : to.timeoutStart) + to.timeoutDuration;

    return applicable.reduce((p, c) => {
        if (p && getTimeoutEnd(p) > getTimeoutEnd(c)) {
            return p;
        }
        return c;
    });
}